import { createElementVNode as _createElementVNode, unref as _unref, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, withModifiers as _withModifiers, normalizeClass as _normalizeClass, createVNode as _createVNode, normalizeStyle as _normalizeStyle, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { class: "card border" }
const _hoisted_2 = {
  key: 0,
  class: "p-3 text-center"
}
const _hoisted_3 = {
  key: 1,
  class: "p-3 text-center"
}
const _hoisted_4 = {
  key: 3,
  class: "locations-list"
}
const _hoisted_5 = { class: "location-main" }
const _hoisted_6 = { class: "location-header" }
const _hoisted_7 = { class: "location-info" }
const _hoisted_8 = { class: "location-name d-flex align-items-center" }
const _hoisted_9 = { class: "fw-bold" }
const _hoisted_10 = { class: "location-address" }
const _hoisted_11 = ["href"]
const _hoisted_12 = { class: "d-flex gap-3" }
const _hoisted_13 = {
  key: 0,
  class: "location-plan"
}
const _hoisted_14 = ["href"]
const _hoisted_15 = {
  key: 1,
  class: "location-images"
}
const _hoisted_16 = ["onClick"]
const _hoisted_17 = {
  key: 0,
  class: "location-status mt-1"
}
const _hoisted_18 = { class: "text-muted small" }
const _hoisted_19 = {
  key: 1,
  class: "location-hours mt-1 small text-muted"
}
const _hoisted_20 = ["onClick"]
const _hoisted_21 = { class: "d-flex justify-content-between align-items-center mb-2" }
const _hoisted_22 = { class: "mb-0 fw-bold" }
const _hoisted_23 = {
  key: 0,
  class: "py-3"
}
const _hoisted_24 = { class: "opening-hours-skeleton" }
const _hoisted_25 = { class: "opening-hours-list" }
const _hoisted_26 = { class: "d-flex justify-content-between align-items-start" }
const _hoisted_27 = { class: "day-info" }
const _hoisted_28 = { class: "fw-bold small" }
const _hoisted_29 = {
  class: "text-muted",
  style: {"font-size":"0.75rem"}
}
const _hoisted_30 = { class: "hours-info text-end" }
const _hoisted_31 = {
  key: 0,
  class: "small"
}
const _hoisted_32 = {
  key: 1,
  class: "small text-muted"
}

import { ref, computed, onMounted, onUnmounted } from "vue";
import { useLocationData } from "../../composables/useLocationData.js";
import { useBuildingOpeningHoursQuery } from "../../api/queries/userQueries.js";
import { useFloating, autoUpdate, offset, flip, shift } from "@floating-ui/vue";
import LocationsSkeleton from "./LocationsSkeleton.vue";
import ImageViewer from "../../../application/utility/ImageViewer.vue";

// Use the location data composable directly

export default {
  __name: 'LocationsTable',
  setup(__props) {

const { locations, isLoading, isError } = useLocationData({
	refreshInterval: 1000 * 60 * 2, // 2 minutes
	autoRefresh: true,
});

// Image viewer instance
const imageViewer = ref(null);

// Opening hours popover state
const openingHoursPopoverOpen = ref(false);
const activeLocationId = ref(null);
const openingHoursButtonRefs = ref({});
const openingHoursPopoverEl = ref(null);

// Opening hours query - need to pass the actual value, not the ref
const openingHoursQuery = useBuildingOpeningHoursQuery(
	computed(() => activeLocationId.value)
);
const openingHoursData = computed(() => openingHoursQuery.data?.value);
const isLoadingOpeningHours = computed(
	() => openingHoursQuery.isLoading?.value
);
const openingHoursError = computed(() => openingHoursQuery.error?.value);

// Setup floating UI for opening hours popover
const { floatingStyles: openingHoursPopoverStyles } = useFloating(
	computed(() => openingHoursButtonRefs.value[activeLocationId.value]),
	openingHoursPopoverEl,
	{
		middleware: [offset(10), flip(), shift({ padding: 5 })],
		whileElementsMounted: autoUpdate,
	}
);

// Popover functions
const setOpeningHoursButtonRef = (locationId, el) => {
	if (el) {
		openingHoursButtonRefs.value[locationId] = el;
	}
};

const setOpeningHoursPopoverRef = (el) => {
	openingHoursPopoverEl.value = el;
};

const toggleOpeningHoursPopover = (locationId) => {
	if (openingHoursPopoverOpen.value && activeLocationId.value === locationId) {
		openingHoursPopoverOpen.value = false;
		activeLocationId.value = null;
	} else {
		activeLocationId.value = locationId;
		openingHoursPopoverOpen.value = true;
	}
};

const closeOpeningHoursPopover = () => {
	openingHoursPopoverOpen.value = false;
	activeLocationId.value = null;
};

// Close popover when clicking outside
const handleClickOutside = (event) => {
	if (
		openingHoursPopoverOpen.value &&
		openingHoursPopoverEl.value &&
		activeLocationId.value &&
		!openingHoursPopoverEl.value.contains(event.target) &&
		!openingHoursButtonRefs.value[activeLocationId.value]?.contains(
			event.target
		)
	) {
		closeOpeningHoursPopover();
	}
};

// Helper functions
const getLocationName = (locationId) => {
	const location = locations.value.find((loc) => loc.id === locationId);
	return location?.name || "";
};

const formatDayName = (dateKey) => {
	const date = new Date(dateKey);
	const today = new Date().toISOString().split("T")[0];
	const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000)
		.toISOString()
		.split("T")[0];

	if (dateKey === today) return "Heute";
	if (dateKey === tomorrow) return "Morgen";

	const weekdays = [
		"Sonntag",
		"Montag",
		"Dienstag",
		"Mittwoch",
		"Donnerstag",
		"Freitag",
		"Samstag",
	];
	return weekdays[date.getDay()];
};

const formatDate = (dateKey) => {
	const date = new Date(dateKey);
	return date.toLocaleDateString("de-DE", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
	});
};

const formatTime = (timeData) => {
	let time;

	if (typeof timeData === "string") {
		// RFC3339 format
		time = new Date(timeData);
	} else if (timeData && typeof timeData === "object" && timeData.date) {
		// PHP DateTime object format
		// Parse the date string and handle timezone
		const dateStr = timeData.date.replace(" ", "T");
		if (timeData.timezone && timeData.timezone !== "UTC") {
			// For Europe/Berlin timezone, we need to handle it properly
			time = new Date(dateStr);
		} else {
			time = new Date(dateStr + "Z"); // UTC
		}
	} else {
		// Fallback
		time = new Date(timeData);
	}

	const formatted = time.toLocaleTimeString("de-DE", {
		hour: "2-digit",
		minute: "2-digit",
		timeZone: "Europe/Berlin",
	});

	return formatted;
};

const formatDayOpeningHours = (sessions) => {
	if (!sessions || sessions.length === 0) {
		return "Geschlossen";
	}

	// Process all sessions for the day
	const times = sessions.map((session) => ({
		start: formatTime(session.startTime),
		end: formatTime(session.endTime),
	}));

	// Find the earliest start and latest end
	const allStartTimes = times.map((t) => t.start);
	const allEndTimes = times.map((t) => t.end);

	const earliestStart = allStartTimes.sort()[0];
	const latestEnd = allEndTimes.sort().reverse()[0];

	// Check for 24-hour opening
	if (
		(earliestStart === "00:00" && latestEnd === "00:00") ||
		(earliestStart === "00:00" && latestEnd === "23:59")
	) {
		return "Ganztägig geöffnet";
	}

	// Handle midnight end times
	let displayEnd = latestEnd;
	if (latestEnd === "00:00" && earliestStart !== "00:00") {
		displayEnd = "24:00";
	}

	return `${earliestStart} - ${displayEnd} Uhr`;
};

const isLastDay = (dateKey, openingHours) => {
	const keys = Object.keys(openingHours);
	return keys[keys.length - 1] === dateKey;
};

// The location objects from useLocationData already have processed status
const getLocationItemClass = (location) => {
	return {
		"location-open": location.isOpen,
		"location-closed": !location.isOpen,
	};
};

const getSimplifiedTime = (location) => {
	// The nextAvailable field is already formatted
	return location.nextAvailable || "";
};

// Lifecycle hooks
onMounted(() => {
	document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
	document.removeEventListener("click", handleClickOutside);
});

return (_ctx, _cache) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _cache[6] || (_cache[6] = _createElementVNode("div", { class: "card-header bg-white border-0 p-2" }, [
        _createElementVNode("h6", { class: "mb-0 fw-bold" }, " Standorte ")
      ], -1 /* CACHED */)),
      (_unref(isError))
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [...(_cache[0] || (_cache[0] = [
            _createElementVNode("div", { class: "text-danger small" }, [
              _createElementVNode("i", { class: "fas fa-exclamation-triangle me-1" }),
              _createTextVNode(" Fehler beim Laden der Standortdaten ")
            ], -1 /* CACHED */)
          ]))]))
        : (!_unref(locations).length && !_unref(isLoading))
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [...(_cache[1] || (_cache[1] = [
              _createElementVNode("div", { class: "text-muted small" }, " Keine Standorte verfügbar ", -1 /* CACHED */)
            ]))]))
          : (_unref(isLoading))
            ? (_openBlock(), _createBlock(LocationsSkeleton, { key: 2 }))
            : (_openBlock(), _createElementBlock("div", _hoisted_4, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(locations), (location) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: location.id,
                    class: _normalizeClass([getLocationItemClass(location), "location-item"])
                  }, [
                    _createElementVNode("div", _hoisted_5, [
                      _createElementVNode("div", _hoisted_6, [
                        _createElementVNode("div", _hoisted_7, [
                          _createElementVNode("div", _hoisted_8, [
                            _cache[2] || (_cache[2] = _createElementVNode("i", { class: "fas fa-map-marker-alt me-2" }, null, -1 /* CACHED */)),
                            _createElementVNode("span", _hoisted_9, _toDisplayString(location.name) + " (" + _toDisplayString(location.short) + ")", 1 /* TEXT */)
                          ]),
                          _createElementVNode("div", _hoisted_10, [
                            _createElementVNode("a", {
                              href: 
                    'https://maps.google.com/?q=' +
                      encodeURIComponent(location.address)
                  ,
                              target: "_blank"
                            }, [
                              _cache[3] || (_cache[3] = _createElementVNode("i", { class: "fas fa-diamond-turn-right me-1" }, null, -1 /* CACHED */)),
                              _createTextVNode(_toDisplayString(location.address), 1 /* TEXT */)
                            ], 8 /* PROPS */, _hoisted_11)
                          ]),
                          _createElementVNode("div", _hoisted_12, [
                            (location.planUrl)
                              ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                                  _createElementVNode("a", {
                                    href: location.planUrl,
                                    target: "_blank"
                                  }, [...(_cache[4] || (_cache[4] = [
                                    _createElementVNode("i", { class: "fa-regular fa-file-pdf me-1" }, null, -1 /* CACHED */),
                                    _createTextVNode("Gebäudeplan ", -1 /* CACHED */)
                                  ]))], 8 /* PROPS */, _hoisted_14)
                                ]))
                              : _createCommentVNode("v-if", true),
                            (location.images.length)
                              ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                                  _createElementVNode("a", {
                                    href: "#",
                                    onClick: _withModifiers($event => (imageViewer.value.openImages(location.images, location.name)), ["prevent"])
                                  }, [...(_cache[5] || (_cache[5] = [
                                    _createElementVNode("i", { class: "fa-regular fa-image me-1" }, null, -1 /* CACHED */),
                                    _createTextVNode("Bilder ", -1 /* CACHED */)
                                  ]))], 8 /* PROPS */, _hoisted_16)
                                ]))
                              : _createCommentVNode("v-if", true)
                          ]),
                          (location.isOpen && location.availableRooms)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
                                _createElementVNode("span", _hoisted_18, _toDisplayString(location.availableRooms) + " Räume verfügbar ", 1 /* TEXT */)
                              ]))
                            : _createCommentVNode("v-if", true),
                          (getSimplifiedTime(location))
                            ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                                _createTextVNode(_toDisplayString(getSimplifiedTime(location)) + " • ", 1 /* TEXT */),
                                _createElementVNode("a", {
                                  ref_for: true,
                                  ref: (el) => setOpeningHoursButtonRef(location.id, el),
                                  title: 'Alle Öffnungszeiten anzeigen',
                                  href: "#",
                                  style: {"cursor":"pointer"},
                                  onClick: _withModifiers($event => (toggleOpeningHoursPopover(location.id)), ["prevent"])
                                }, " Details ", 8 /* PROPS */, _hoisted_20)
                              ]))
                            : _createCommentVNode("v-if", true)
                        ])
                      ])
                    ])
                  ], 2 /* CLASS */))
                }), 128 /* KEYED_FRAGMENT */))
              ]))
    ]),
    _createVNode(ImageViewer, {
      ref_key: "imageViewer",
      ref: imageViewer
    }, null, 512 /* NEED_PATCH */),
    _createCommentVNode(" Opening Hours Popover "),
    (openingHoursPopoverOpen.value && activeLocationId.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          ref: (el) => setOpeningHoursPopoverRef(el),
          style: _normalizeStyle([_unref(openingHoursPopoverStyles), {"z-index":"1050","max-width":"320px","max-height":"400px","overflow-y":"auto"}]),
          class: "popover-content bg-white border shadow-sm rounded p-3"
        }, [
          _createElementVNode("div", _hoisted_21, [
            _createElementVNode("h6", _hoisted_22, _toDisplayString(getLocationName(activeLocationId.value)) + " - Öffnungszeiten ", 1 /* TEXT */)
          ]),
          _createCommentVNode(" Loading state "),
          (isLoadingOpeningHours.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_23, [
                _cache[8] || (_cache[8] = _createElementVNode("div", { class: "small text-muted mb-3" }, [
                  _createElementVNode("div", { class: "skeleton skeleton-popover-subtitle" })
                ], -1 /* CACHED */)),
                _createElementVNode("div", _hoisted_24, [
                  (_openBlock(), _createElementBlock(_Fragment, null, _renderList(7, (n) => {
                    return _createElementVNode("div", {
                      key: n,
                      class: "opening-hours-day-skeleton mb-2 pb-2"
                    }, [...(_cache[7] || (_cache[7] = [
                      _createStaticVNode("<div class=\"d-flex justify-content-between align-items-start\"><div class=\"day-info-skeleton\"><div class=\"skeleton skeleton-day-name mb-1\"></div><div class=\"skeleton skeleton-date\"></div></div><div class=\"hours-info-skeleton text-end\"><div class=\"skeleton skeleton-hours\"></div></div></div>", 1)
                    ]))])
                  }), 64 /* STABLE_FRAGMENT */))
                ])
              ]))
            : (openingHoursError.value)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                  _createCommentVNode(" Error state "),
                  _cache[9] || (_cache[9] = _createElementVNode("div", { class: "text-danger small" }, " Fehler beim Laden der Öffnungszeiten ", -1 /* CACHED */))
                ], 2112 /* STABLE_FRAGMENT, DEV_ROOT_FRAGMENT */))
              : (openingHoursData.value)
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                    _createCommentVNode(" Opening hours table "),
                    _createElementVNode("div", null, [
                      _cache[10] || (_cache[10] = _createElementVNode("div", { class: "small text-muted mb-3" }, " Heute und die nächsten 14 Tage: ", -1 /* CACHED */)),
                      _createElementVNode("div", _hoisted_25, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(openingHoursData.value.openingHours, (dayData, dateKey) => {
                          return (_openBlock(), _createElementBlock("div", {
                            key: dateKey,
                            class: _normalizeClass(["opening-hours-day mb-2 pb-2", {
            'border-bottom': !isLastDay(dateKey, openingHoursData.value.openingHours),
          }])
                          }, [
                            _createElementVNode("div", _hoisted_26, [
                              _createElementVNode("div", _hoisted_27, [
                                _createElementVNode("div", _hoisted_28, _toDisplayString(formatDayName(dateKey)), 1 /* TEXT */),
                                _createElementVNode("div", _hoisted_29, _toDisplayString(formatDate(dateKey)), 1 /* TEXT */)
                              ]),
                              _createElementVNode("div", _hoisted_30, [
                                (dayData.sessions && dayData.sessions.length > 0)
                                  ? (_openBlock(), _createElementBlock("div", _hoisted_31, _toDisplayString(formatDayOpeningHours(dayData.sessions)), 1 /* TEXT */))
                                  : (_openBlock(), _createElementBlock("div", _hoisted_32, " Geschlossen "))
                              ])
                            ])
                          ], 2 /* CLASS */))
                        }), 128 /* KEYED_FRAGMENT */))
                      ])
                    ])
                  ], 2112 /* STABLE_FRAGMENT, DEV_ROOT_FRAGMENT */))
                : _createCommentVNode("v-if", true)
        ], 4 /* STYLE */))
      : _createCommentVNode("v-if", true)
  ], 64 /* STABLE_FRAGMENT */))
}
}

}